import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMountain, FaChild, FaPaw, FaLandmark, FaPaintBrush, FaTree, FaBuilding, FaEye } from 'react-icons/fa';
import Modal from './Modal';
import '../styles/FilterModal.css';

const DEFAULT_FILTERS = {
  duration: 'classic',
  terrain: [],
  interests: [],
  radius: 2000,
  openNow: false,
};

function FilterModal({ isOpen, onClose, onApply }) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const toggleChip = (key, value) => {
    setFilters(prev => {
      const list = prev[key];
      return {
        ...prev,
        [key]: list.includes(value) ? list.filter(v => v !== value) : [...list, value],
      };
    });
  };

  const handleClear = () => setFilters(DEFAULT_FILTERS);

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const durations = [
    { id: 'quick',     label: t('filter_modal.duration_quick'),     name: t('filter_modal.duration_quick_name'),     time: '15 min' },
    { id: 'classic',   label: t('filter_modal.duration_classic'),   name: t('filter_modal.duration_classic_name'),   time: '30 min' },
    { id: 'leisurely', label: t('filter_modal.duration_leisurely'), name: t('filter_modal.duration_leisurely_name'), time: '60 min' },
  ];

  const terrainChips = [
    { id: 'avoid_hills',  icon: FaMountain,   label: t('filter_modal.avoid_hills') },
    { id: 'kid_friendly', icon: FaChild,       label: t('filter_modal.kid_friendly') },
    { id: 'pet_friendly', icon: FaPaw,         label: t('filter_modal.pet_friendly') },
  ];

  const interestChips = [
    { id: 'museums',       icon: FaLandmark,   label: t('filter_modal.museums') },
    { id: 'art_galleries', icon: FaPaintBrush, label: t('filter_modal.art_galleries') },
    { id: 'parks',         icon: FaTree,       label: t('filter_modal.parks') },
    { id: 'monuments',     icon: FaBuilding,   label: t('filter_modal.monuments') },
    { id: 'viewpoints',    icon: FaEye,        label: t('filter_modal.viewpoints') },
  ];

  const radiusKm = Math.round(filters.radius / 1000);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="filter-content">
        <div className="filter-header">
          <h2>{t('filter_modal.title')}</h2>
          <p>{t('filter_modal.subtitle')}</p>
        </div>
        <hr className="filter-divider" />

        <div className="filter-section">
          <h3>{t('filter_modal.duration_title')}</h3>
          <div className="duration-cards">
            {durations.map(d => (
              <div
                key={d.id}
                className={`duration-card ${filters.duration === d.id ? 'selected' : ''}`}
                onClick={() => setFilters(prev => ({ ...prev, duration: d.id }))}
              >
                <div className="duration-card-top">
                  <span className="duration-label">{d.label}</span>
                  <span className={`duration-radio ${filters.duration === d.id ? 'checked' : ''}`} />
                </div>
                <span className="duration-name">{d.name}</span>
                <span className="duration-time">{d.time}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h3>{t('filter_modal.terrain_title')}</h3>
          <div className="chip-group">
            {terrainChips.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                className={`filter-chip ${filters.terrain.includes(id) ? 'selected' : ''}`}
                onClick={() => toggleChip('terrain', id)}
              >
                <Icon className="chip-icon" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h3>{t('filter_modal.interests_title')}</h3>
          <div className="chip-group">
            {interestChips.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                className={`filter-chip ${filters.interests.includes(id) ? 'selected' : ''}`}
                onClick={() => toggleChip('interests', id)}
              >
                <Icon className="chip-icon" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <hr className="filter-divider" />

        <div className="filter-section">
          <h3>{t('filter_modal.route_settings_title')}</h3>
          <p className="filter-section-subtitle">{t('filter_modal.route_settings_subtitle')}</p>
          <div className="radius-slider-row">
            <span className="radius-label">1 km</span>
            <input
              type="range"
              min={1000}
              max={5000}
              step={500}
              value={filters.radius}
              onChange={(e) => setFilters(prev => ({ ...prev, radius: Number(e.target.value) }))}
              className="radius-slider"
            />
            <span className="radius-label">5 km</span>
          </div>
          <div className="radius-value">{radiusKm} km</div>

          <div className="open-now-row">
            <span>{t('filter_modal.open_now')}</span>
            <button
              className={`toggle-switch ${filters.openNow ? 'on' : ''}`}
              onClick={() => setFilters(prev => ({ ...prev, openNow: !prev.openNow }))}
              role="switch"
              aria-checked={filters.openNow}
            >
              <span className="toggle-thumb" />
            </button>
          </div>
        </div>

        <div className="filter-footer">
          <button className="filter-clear-btn" onClick={handleClear}>
            {t('filter_modal.clear_all')}
          </button>
          <button className="filter-apply-btn" onClick={handleApply}>
            {t('filter_modal.apply_filters')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default FilterModal;
